import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function Home() {
  const [activeSection, setActiveSection] = useState('banner-part');

  // Handle smooth scrolling for page-scroll links
  useEffect(() => {
    const handlePageScroll = (e) => {
      e.preventDefault();
      const targetId = e.currentTarget.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        // Get navbar height dynamically
        const navbar = document.querySelector('.navbar');
        const navbarHeight = navbar ? navbar.offsetHeight : 80; // fallback to 80px
        
        // Calculate scroll position with offset
        const targetPosition = targetElement.offsetTop - navbarHeight - 20; // 20px extra spacing
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    };

    const pageScrollLinks = document.querySelectorAll('.page-scroll');
    pageScrollLinks.forEach(link => {
      link.addEventListener('click', handlePageScroll);
    });

    return () => {
      pageScrollLinks.forEach(link => {
        link.removeEventListener('click', handlePageScroll);
      });
    };
  }, []);

  // Scroll spy for active section detection
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const sections = ['banner-part', 'data-solutions-part', 'cloud-part', 'use-cases-part'];
          const navbar = document.querySelector('.navbar');
          const navbarHeight = navbar ? navbar.offsetHeight : 80;
          const scrollPosition = window.scrollY + navbarHeight + 50; // Add offset for better detection
          
          let currentSection = 'banner-part';
          
          sections.forEach(sectionId => {
            const section = document.getElementById(sectionId);
            if (section) {
              const sectionTop = section.offsetTop;
              const sectionBottom = sectionTop + section.offsetHeight;
              
              if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                currentSection = sectionId;
              }
            }
          });
          
          setActiveSection(currentSection);
          
          // Header animation - add/remove header-fixed class
          const header = document.querySelector('header');
          if (header) {
            if (window.scrollY > 0) {
              header.classList.add('header-fixed');
            } else {
              header.classList.remove('header-fixed');
            }
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once on mount
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isActive = (sectionId) => activeSection === sectionId;

  return (
    <div className="main">
      {/* Header start */}
      <header id="header">
        <div className="container">
          <div className="row m-0">
            <div className="col-lg-3 col-md-4 col-8 p-0">
              <div className="navbar-header" style={{paddingLeft: '0'}}>
                <Link className="navbar-brand" to="/">
                  <img alt="dptech" src="images/dptech_logo_white.png" style={{height: '60px'}} />
                </Link> 
              </div>
            </div>
            <div className="col-lg-9 col-md-8 col-4 p-0">
              <button 
                data-target=".navbar-collapse" 
                data-toggle="collapse" 
                className="navbar-toggle d-block d-lg-none d-xl-none" 
                type="button"
                aria-label="Toggle navigation menu"
                aria-expanded="false"
                aria-controls="navbar-collapse"
              >
                <i className="fa fa-bars"></i>
              </button>
              <div className="overlay"></div>
              <div id="menu" className="navbar-collapse collapse">
                <ul className="nav navbar-nav">
                  <li className="level">
                    <a href="#banner-part" className={`page-scroll ${isActive('banner-part') ? 'active' : ''}`}>
                      Home
                    </a>
                  </li>
                  <li className="level">
                    <a href="#data-solutions-part" className={`page-scroll ${isActive('data-solutions-part') ? 'active' : ''}`}>
                      Data solutions
                    </a>
                  </li>
                  <li className="level">
                    <a href="#cloud-part" className={`page-scroll ${isActive('cloud-part') ? 'active' : ''}`}>
                      Cloud
                    </a>
                  </li>
                  <li className="level">
                    <a href="#use-cases-part" className={`page-scroll ${isActive('use-cases-part') ? 'active' : ''}`}>
                      Use case
                    </a>
                  </li>
                  <li className="level">
                    <Link to="/about">About</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>
      {/* Header end */}

      {/* Banner start */}
      <section id="banner-part" className="menu-section main-banner">
        {/* Video Background */}
        <video autoPlay muted loop playsInline id="banner-video">
          <source src="videos/your-video.mp4" type="video/mp4" />
          <source src="videos/your-video.webm" type="video/webm" />
          Your browser does not support HTML5 video.
        </video>
        {/* Video Overlay */}
        <div className="video-overlay"></div>
        
        <div className="banner">
          <div className="banner-detail">
            <div className="container">
              <div className="row m-0">
                <div className="col-lg-6 p-0 fadeInLeft">
                  <div className="banner-detail-inner text-center text-lg-left">
                    <h1 className="banner-title mt-4 mt-lg-0">Il tuo partner per l'innovazione</h1>
                    <p>
                      <span style={{color: '#FFD84D', fontWeight: '600'}}>Trasformiamo i dati in valore per il tuo business.</span> Sviluppiamo soluzioni AI e cloud-native personalizzate per aziende che innovano.
                    </p>
                    <br />
                    <Link to="/about" className="btn big-width btn-color d-block d-lg-inline-block">
                      <b>Collabora con noi</b>
                    </Link>
                  </div>
                </div>
                <div className="col-lg-6 p-0">
                  <div className="banner-main-img">
                    <div className="front-bg fadeInRight">
                      <img src="images/isometric_machine_learning_blue.png" alt="dptech - data analytics" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Banner end */}

      {/* Data Solutions section start */}
      <section id="data-solutions-part" className="menu-section about-part position-r ptb-100" style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f8ff 100%)', paddingTop: '60px'}}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
              <div className="heading-part text-center mb-40 mb-sm-30 mb-xs-20">
                <div className="inline-header">
                  <h2 className="main_title">Data Solutions</h2>
                  <div className="header-separator"></div>
                  <p className="header-subtitle">Soluzioni su misura per scalare il tuo business con AI, cloud-native e automazione intelligente</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb_-30">
            {/* Unified Solutions Grid - Single Row with 3 Merged Elements */}
            <div className="row">
              <div className="col-lg-4 mb-4 fadeInUpMedium delay3">
                <div className="data-solution-card" style={{background: '#fff', borderRadius: '20px', padding: '25px', boxShadow: '0 8px 25px rgba(0,0,0,0.08)', transition: 'all 0.2s ease', border: '1px solid rgba(0,0,0,0.05)', height: '100%'}} onMouseOver={(e) => {e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 30px rgba(0,0,0,0.12)'}} onMouseOut={(e) => {e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 25px rgba(0,0,0,0.08)'}}>
                  <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <div style={{background: 'linear-gradient(135deg, #FFD84D, #FFA500)', width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px'}}>
                      <i className="fa fa-robot" style={{fontSize: '1.8rem', color: '#000'}}></i>
                    </div>
                    <h4 style={{color: '#333', marginBottom: '12px'}}>Data & AI</h4>
                  </div>
                  <p style={{color: '#555', lineHeight: '1.6', textAlign: 'center'}}>Piattaforme AI-driven, analisi avanzate, integrazione dati da fonti multiple. Trasforma i tuoi dati in insight azionabili con GenAI e Machine Learning</p>
                </div>
              </div>
              
              <div className="col-lg-4 mb-4 fadeInUpMedium delay4">
                <div className="data-solution-card" style={{background: '#fff', borderRadius: '20px', padding: '25px', boxShadow: '0 8px 25px rgba(0,0,0,0.08)', transition: 'all 0.2s ease', border: '1px solid rgba(0,0,0,0.05)', height: '100%'}} onMouseOver={(e) => {e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 30px rgba(0,0,0,0.12)'}} onMouseOut={(e) => {e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 25px rgba(0,0,0,0.08)'}}>
                  <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <div style={{background: 'linear-gradient(135deg, #0090bf, #0066cc)', width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px'}}>
                      <i className="fa fa-cloud" style={{fontSize: '1.8rem', color: '#fff'}}></i>
                    </div>
                    <h4 style={{color: '#333', marginBottom: '12px'}}>Cloud</h4>
                  </div>
                  <p style={{color: '#555', lineHeight: '1.6', textAlign: 'center'}}>Architetture cloud-native su AWS e GCP, serverless e managed services. Soluzioni multi-provider specializzate, ottimizzate per performance, costi e resilienza</p>
                </div>
              </div>
              
              <div className="col-lg-4 mb-4 fadeInUpMedium delay5">
                <div className="data-solution-card" style={{background: '#fff', borderRadius: '20px', padding: '25px', boxShadow: '0 8px 25px rgba(0,0,0,0.08)', transition: 'all 0.2s ease', border: '1px solid rgba(0,0,0,0.05)', height: '100%'}} onMouseOver={(e) => {e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 30px rgba(0,0,0,0.12)'}} onMouseOut={(e) => {e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 25px rgba(0,0,0,0.08)'}}>
                  <div style={{textAlign: 'center', marginBottom: '20px'}}>
                    <div style={{background: 'linear-gradient(135deg, #FFD84D, #FFA500)', width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px'}}>
                      <i className="fa fa-laptop" style={{fontSize: '1.8rem', color: '#000'}}></i>
                    </div>
                    <h4 style={{color: '#333', marginBottom: '12px'}}>Web Application</h4>
                  </div>
                  <p style={{color: '#555', lineHeight: '1.6', textAlign: 'center'}}>Applicazioni web su misura, come CRM e workflow digitali, accessibili e mobile-ready. Soluzioni complete progettate per esigenze specifiche del tuo business</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Data Solutions section end */}

      {/* Cloud section start */}
      <section id="cloud-part" className="menu-section cloud-part position-r ptb-100" style={{paddingBottom: '60px'}}>
        <div className="container">
          <div className="row">
            <div className="col-lg-4 fadeInLeft order-lg-2 order-md-1">
              <div className="about-img text-center text-lg-right fadeInLeft">
                <img src="images/cloud_cert.png" alt="isometric data" height="320px" width="320px" />
              </div>
            </div>
            <div className="col-lg-7 order-lg-1 order-md-2 d-flex align-items-center">
              <div className="heading-part text-center mb-50 mb-sm-30 mb-xs-20" style={{width: '100%', paddingLeft: '20px'}}>
                <h2 className="main_title">Esperienza certificata</h2>
                <span style={{color: '#FFD84D'}}>Progettiamo e sviluppiamo soluzioni cloud-native su AWS e GCP per ogni fase del tuo percorso data-driven</span>
                <h4 style={{color: '#fff', paddingTop: '25px'}}>
                  •	Applicazioni cloud-native serverless, integrazione servizi managed, IoT industriale e consumer
                </h4>
                <h3 style={{color: '#fff'}}>
                  •	Soluzioni ML su AWS e GCP, pipeline dati, automazione e insight in tempo reale
                </h3>
                <h3 style={{color: '#fff'}}>
                  •	Strumenti che si integrano facilmente nei processi aziendali esistenti grazie a API e SDK
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Cloud section end */}

      {/* Use Case section start */}
      <section id="use-cases-part" className="menu-section team-part position-r ptb-100" style={{background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f8ff 100%)', paddingTop: '60px'}}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
              <div className="heading-part text-center mb-60 mb-sm-40 mb-xs-30">
                <div className="inline-header">
                  <h2 className="main_title">Use Case</h2>
                  <div className="header-separator"></div>
                  <p className="header-subtitle">Aiutiamo a trasformare il modo in cui le aziende gestiscono i dati e l'automazione</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb_-30">
            {/* SmartDD and IoT on same line - Enhanced Design */}
            <div className="row">
              {/* SmartDD Card */}
              <div className="col-lg-6 fadeInUpMedium delay1" style={{marginBottom: '0'}}>
                <div className="use-case-card" style={{background: 'linear-gradient(135deg, #0090bf 0%, #0066cc 100%)', borderRadius: '25px', padding: '35px', boxShadow: '0 8px 25px rgba(0, 144, 191, 0.08)', position: 'relative', overflow: 'hidden', height: '100%', transition: 'all 0.2s ease'}} onMouseOver={(e) => {e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 30px rgba(0, 144, 191, 0.12)'}} onMouseOut={(e) => {e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 25px rgba(0, 144, 191, 0.08)'}}>
                  <div style={{position: 'absolute', top: '-20px', right: '-20px', background: 'rgba(255,255,255,0.15)', width: '180px', height: '180px', borderRadius: '50%'}}></div>
                  <div style={{position: 'absolute', bottom: '-25px', left: '-25px', background: 'rgba(255,255,255,0.08)', width: '120px', height: '120px', borderRadius: '50%'}}></div>
                  
                  <div style={{position: 'relative', zIndex: 2}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '22px', justifyContent: 'flex-start'}}>
                      <img src="images/smartdd_logo_std.png" alt="SmartDD Logo" style={{width: '50px', height: 'auto'}} />
                      <h1 style={{color: '#fff', margin: 0}}>SmartDD</h1>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.95)', display: 'block', padding: '8px 20px', borderRadius: '25px', marginBottom: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', textAlign: 'left', width: 'fit-content'}}>
                      <span style={{color: '#000', letterSpacing: '1.2px'}}><i className="fa fa-laptop"></i> Web App</span>
                    </div>
                    <p style={{color: '#fff', marginBottom: '25px', lineHeight: '1.6', fontSize: '16px'}}>Ottieni in pochi click <b>dati strutturati dai tuoi documenti</b>. <i>SmartDataDocument</i> impara dai tuoi documenti i layout, formati e strutture specifiche, riducendo il lavoro manuale e aumentando la precisione dell'estrazione.</p>
                    
                    {/* Visual Workflow Diagram */}
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px'}}>
                      {/* Step 1: Multiple Documents */}
                      <div style={{textAlign: 'center', flex: 1}}>
                        <div style={{width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px'}}>
                          <i className="fa fa-files-o" style={{fontSize: '1.8rem', color: '#fff'}}></i>
                        </div>
                        <span style={{color: '#fff', fontSize: '13px', fontWeight: '600'}}>Documenti di vari tipi</span>
                      </div>
                      
                      {/* Arrow 1 */}
                      <div style={{flex: '0 0 auto', margin: '0 10px'}}>
                        <i className="fa fa-arrow-right" style={{color: '#fff', fontSize: '1.2rem'}}></i>
                      </div>
                      
                      {/* Step 2: AI Processing */}
                      <div style={{textAlign: 'center', flex: 1}}>
                        <div style={{width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px'}}>
                          <i className="fa fa-magic" style={{fontSize: '1.8rem', color: '#fff'}}></i>
                        </div>
                        <span style={{color: '#fff', fontSize: '13px', fontWeight: '600'}}>Estrazione con AI</span>
                      </div>
                      
                      {/* Arrow 2 */}
                      <div style={{flex: '0 0 auto', margin: '0 10px'}}>
                        <i className="fa fa-arrow-right" style={{color: '#fff', fontSize: '1.2rem'}}></i>
                      </div>
                      
                      {/* Step 3: Structured Data */}
                      <div style={{textAlign: 'center', flex: 1}}>
                        <div style={{width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px'}}>
                          <i className="fa fa-table" style={{fontSize: '1.8rem', color: '#fff'}}></i>
                        </div>
                        <span style={{color: '#fff', fontSize: '13px', fontWeight: '600'}}>Dati strutturati</span>
                      </div>
                    </div>
                    
                    <a href="https://smartdd.it" target="_blank" rel="noopener noreferrer" className="btn btn-color smartdd-cta" style={{padding: '15px 30px', borderRadius: '30px', background: '#fff', color: '#0090bf', border: 'none', transition: 'all 0.3s ease', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', width: '100%', textDecoration: 'none', display: 'inline-block', cursor: 'pointer'}} onMouseOver={(e) => {e.currentTarget.style.background='#FF5DA2'; e.currentTarget.style.color='#fff'; e.currentTarget.style.borderColor='#FF5DA2'; e.currentTarget.querySelector('.fa-search').style.color='#fff'; e.currentTarget.querySelector('.smartdd-text').style.color='#fff'}} onMouseOut={(e) => {e.currentTarget.style.background='#fff'; e.currentTarget.style.color='#0090bf'; e.currentTarget.style.borderColor='none'; e.currentTarget.querySelector('.fa-search').style.color='#0090bf'; e.currentTarget.querySelector('.smartdd-text').style.color='#ff5da2'}}>
                      <i className="fa fa-search" style={{color: '#0090bf'}}></i> Scopri di più su <span className="smartdd-text" style={{fontWeight: 'bold', color: '#ff5da2', transition: 'all 0.3s ease'}}>SmartDD.it</span>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Data Harmonization Card */}
              <div className="col-lg-6 fadeInUpMedium delay2" style={{marginTop: '0'}}>
                <div className="use-case-card" style={{background: 'rgb(33,33,33)', borderRadius: '20px', padding: '4px', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)', position: 'relative', overflow: 'hidden', transition: 'all 0.2s ease'}} onMouseOver={(e) => {e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 30px rgba(0, 0, 0, 0.2)'}} onMouseOut={(e) => {e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 8px 25px rgba(0, 0, 0, 0.15)'}}>
                  <div style={{position: 'absolute', top: '-20px', right: '-20px', background: 'rgba(255,255,255,0.25)', width: '120px', height: '120px', borderRadius: '50%'}}></div>
                  <div style={{position: 'absolute', bottom: '-25px', left: '-25px', background: 'rgba(255,255,255,0.15)', width: '80px', height: '80px', borderRadius: '50%'}}></div>
                  
                  <div style={{position: 'relative', zIndex: 2, background: '#fff', borderRadius: '12px', marginBottom: '0', padding: '25px', paddingBottom: '35px', display: 'flex', flexDirection: 'column'}}>
                    {/* Title with Icon */}
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px'}}>
                      <i className="fa fa-magic" style={{color: '#0090bf', fontSize: '24px'}}></i>
                      <h3 style={{color: '#1a1a1a', fontSize: '20px', fontWeight: '700', margin: 0, textAlign: 'left', lineHeight: '1.3'}}>Servizio di Data Harmonization</h3>
                    </div>
                    
                    {/* Description */}
                    <p style={{color: '#333', marginBottom: '0px', lineHeight: '1.6', fontSize: '14px', textAlign: 'left'}}>
                      Le potenzialità della Generative AI usate per introdurre meccanismi di data quality avanzata e adattiva: controllo, arricchimento e validazione del dato direttamente all'interno delle data pipeline.
                    </p>
                    
                    {/* Bottom section with keywords and CTA */}
                    <div className="use-case-bottom-section" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '3px'}}>
                      {/* Keywords */}
                      <div style={{display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                          <i className="fa fa-database" style={{color: '#666', fontSize: '14px'}}></i>
                          <span style={{color: '#666', fontSize: '13px', fontWeight: '500'}}>Data</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                          <i className="fa fa-cloud" style={{color: '#666', fontSize: '14px'}}></i>
                          <span style={{color: '#666', fontSize: '13px', fontWeight: '500'}}>Cloud GCP</span>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                          <i className="fa fa-magic" style={{color: '#666', fontSize: '14px'}}></i>
                          <span style={{color: '#666', fontSize: '13px', fontWeight: '500'}}>GenAI</span>
                        </div>
                      </div>
                      
                      {/* CTA in basso a destra - inactive */}
                      <div style={{background: '#FFD84D', color: '#1a1a1a', textDecoration: 'none', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', padding: '6px 14px', borderRadius: '20px', position: 'relative', zIndex: 4, boxShadow: '0 2px 8px rgba(255, 216, 77, 0.3)', opacity: 0.6, cursor: 'not-allowed'}}>
                        Use case in via di sviluppo...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Use Case section end */}

      {/* CTA section start */}
      <section className="cta-section position-r ptb-60" style={{background: '#1a1a1a', marginBottom: 0}}>
        <div className="container">
          <div className="row align-items-center justify-content-center">
            <div className="col-12 col-lg-auto mb-3 mb-lg-0 text-center text-lg-left" style={{paddingRight: {lg: '20px'}}}>
              <div className="cta-content-button" style={{paddingRight: {xs: '0', sm: '0', md: '0', lg: '75px'}}}>
                <Link to="/about" className="btn big-width btn-color" style={{margin: {xs: '0 auto', sm: '0 auto', md: '0 auto', lg: '0'}, display: {xs: 'block', sm: 'block', md: 'block', lg: 'inline-block'}}}>
                  <b>Contattaci</b>
                </Link>
              </div>
            </div>
            <div className="col-12 col-lg-auto text-center text-lg-left" style={{paddingLeft: {lg: '20px'}}}>
              <div className="cta-content-text" style={{paddingRight: {xs: '0', sm: '0', md: '0', lg: '35px'}}}>
                <h2 className="main_title" style={{color: '#fff', marginBottom: '15px', whiteSpace: {xs: 'normal', sm: 'normal', md: 'normal', lg: 'nowrap'}}}>
                  <span style={{color: '#FFD84D', fontWeight: '600'}}>dpTech</span> per la tua azienda
                </h2>
                <p style={{color: '#fff', fontSize: '18px', marginBottom: 0, whiteSpace: {xs: 'normal', sm: 'normal', md: 'normal', lg: 'nowrap'}}}>
                  Collabora con noi e sviluppa la tua prossima soluzione innovativa
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA section end */}

      {/* Footer section start */}
      <footer style={{background: '#f8f9fa', color: '#555', padding: '60px 0 20px 0', marginTop: 0}}>
        <div className="container">
          <div className="row">
            {/* Company Info */}
            <div className="col-lg-4 col-md-6 mb-3">
              <div className="footer-section" style={{textAlign: 'left'}}>
                <img src="images/dpTech_logo_1916x710_300dpi_RGB_transparent.png" alt="dpTech" style={{height: '40px', marginBottom: '15px'}} />
                <p style={{color: '#555', lineHeight: '1.5', marginBottom: '15px', fontSize: '14px'}}>
                  dpTech è il tuo partner per l'innovazione. Trasformiamo i dati in valore per il tuo business.
                </p>
              </div>
            </div>
            
            {/* Quick Links */}
            <div className="col-lg-2 col-md-6 mb-3">
              <div className="footer-section" style={{textAlign: 'left'}}>
                <h5 style={{color: '#555', marginBottom: '15px', fontWeight: '600', fontSize: '16px'}}>Servizi</h5>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  <li style={{marginBottom: '8px'}}>
                    <a href="#data-solutions-part" className="page-scroll" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Data Solutions</a>
                  </li>
                  <li style={{marginBottom: '8px'}}>
                    <a href="#cloud-part" className="page-scroll" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Cloud</a>
                  </li>
                  <li style={{marginBottom: '8px'}}>
                    <a href="#use-cases-part" className="page-scroll" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Use Cases</a>
                  </li>
                  <li style={{marginBottom: '8px'}}>
                    <Link to="/about" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Chi Siamo</Link>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Legal & Contact */}
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="footer-section" style={{textAlign: 'left'}}>
                <h5 style={{color: '#555', marginBottom: '15px', fontWeight: '600', fontSize: '16px'}}>Informazioni</h5>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  <li style={{marginBottom: '8px'}}>
                    <Link to="/privacy" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Privacy Policy</Link>
                  </li>
                  <li style={{marginBottom: '8px'}}>
                    <Link to="/cookies" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Cookies Policy</Link>
                  </li>
                  <li style={{marginBottom: '8px'}}>
                    <Link to="/about" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease', fontSize: '14px'}}>Contattaci</Link>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="footer-section" style={{textAlign: 'left'}}>
                <h5 style={{color: '#555', marginBottom: '15px', fontWeight: '600', fontSize: '16px'}}>Contatti</h5>
                <div style={{color: '#555', lineHeight: '1.5'}}>
                  <p style={{marginBottom: '8px', fontSize: '14px'}}>
                    <i className="fas fa-envelope" style={{marginRight: '8px', color: '#FFD84D'}}></i>
                    info@dptech.io
                  </p>
                  <p style={{marginBottom: '8px', fontSize: '14px'}}>
                    <i className="fas fa-map-marker-alt" style={{marginRight: '8px', color: '#FFD84D'}}></i>
                    Milano, Italia
                  </p>
                  <p style={{marginBottom: '8px', fontSize: '14px'}}>
                    <a href="https://www.linkedin.com/company/dptech-io" target="_blank" rel="noopener noreferrer" style={{color: '#555', textDecoration: 'none', transition: 'color 0.3s ease'}}>
                      <i className="fab fa-linkedin" style={{marginRight: '8px', color: '#FFD84D'}}></i>
                      LinkedIn
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer Bottom */}
          <div className="footer-bottom" style={{borderTop: '1px solid #dee2e6', paddingTop: '20px', marginTop: '20px'}}>
            <div className="row align-items-center">
              <div className="col-6 text-left">
                <p style={{color: '#555', margin: 0, fontSize: '13px', lineHeight: '1.4'}}>
                  © 2025 dpTech | Data Solutions. Tutti i diritti riservati.<br />
                  P.IVA: 14159740969
                </p>
              </div>
              <div className="col-6 text-right">
                <div style={{color: '#555', fontSize: '11px', lineHeight: '1.3'}}>
                  <p style={{margin: 0, fontStyle: 'italic', fontFamily: "'Poppins', sans-serif"}}>
                    dptech.io si impegna a seguire le linee guida WCAG 2.1 AA, e a rendere il proprio sito web accessibile.{' '}
                    <a href="/files/Dichiarazione di accessibilita.pdf" target="_blank" rel="noopener noreferrer" style={{color: '#555', textDecoration: 'underline'}}>Dichiarazione di accessibilità</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
      {/* Footer section end */}
    </div>
  );
}

export default Home;
